import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import {
  CancelledReceipt,
  CashRegister,
  CashRegisterBalance,
  CashRegisterBalanceTransaction,
} from '../../interfaces/cash-register';
import {
  CashMovementResult,
  CashReceipt,
  CostCenter,
  Establishment,
  PatientAdvance,
  PostCashBleedRequest,
  PostCashPaymentRequest,
  PostCashPaymentResult,
  PostParkingPaymentRequest,
  TermoTransferencia,
} from '../../interfaces/payment';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private apiURL = environment.apiUrl + '/v' + environment.apiVersion;
  private cashPaymentLocks = new Map<string, Promise<PostCashPaymentResult>>();

  constructor(private httpClient: HttpClient) {}

  public async getEstablishments(username: string, cdEmpresa?: string): Promise<Establishment[]> {
    try {
      const empresaParam = cdEmpresa ? `&cdEmpresa=${encodeURIComponent(cdEmpresa)}` : '';
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Payment/establishments?username=${encodeURIComponent(username)}${empresaParam}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as Establishment[];
    } catch {
      return [];
    }
  }

  public async getCashRegisters(
    username: string,
    options: { unit?: number; sequence?: number },
  ): Promise<CashRegister[]> {
    const params = new URLSearchParams();
    params.set('username', username);
    if (options.unit !== undefined) params.set('unit', String(options.unit));
    if (options.sequence !== undefined) params.set('sequence', String(options.sequence));

    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Payment/cash-registers?${params.toString()}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      const payload = response.data[0];
      return Array.isArray(payload) ? (payload as CashRegister[]) : [payload as CashRegister];
    } catch {
      return [];
    }
  }

  public async postCashPayment(
    idempotencyKey: string,
    request: PostCashPaymentRequest,
  ): Promise<PostCashPaymentResult> {
    const existing = this.cashPaymentLocks.get(idempotencyKey);
    if (existing) return existing;

    const promise = this.executeCashPayment(request);
    this.cashPaymentLocks.set(idempotencyKey, promise);

    promise.finally(() => {
      this.cashPaymentLocks.delete(idempotencyKey);
    });

    return promise;
  }

  public async getCostCenters(establishmentId: number): Promise<CostCenter[]> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Payment/cost-centers?establishmentId=${establishmentId}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as CostCenter[];
    } catch {
      return [];
    }
  }

  public async postCashBleed(request: PostCashBleedRequest): Promise<CashMovementResult> {
    const response = await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/Payment/cash/bleed`, request)
        .pipe(catchError(this.handleError)),
    );
    if (!response?.data?.length) {
      throw new Error('Sangria não retornou resultado.');
    }
    return response.data[0] as CashMovementResult;
  }

  public async postParkingPayment(request: PostParkingPaymentRequest): Promise<CashMovementResult> {
    const response = await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/Payment/cash/parking`, request)
        .pipe(catchError(this.handleError)),
    );
    if (!response?.data?.length) {
      throw new Error('Estacionamento não retornou resultado.');
    }
    return response.data[0] as CashMovementResult;
  }

  public async getBalanceHistory(
    cashRegisterId: number,
    dateStart: string,
    dateEnd: string,
  ): Promise<CashRegisterBalance[]> {
    const params = new URLSearchParams();
    params.set('cashRegisterId', String(cashRegisterId));
    params.set('dateStart', dateStart);
    params.set('dateEnd', dateEnd);

    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Payment/cash-registers/balance?${params.toString()}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as CashRegisterBalance[];
    } catch {
      return [];
    }
  }

  public async getBalanceTransactions(balanceId: number): Promise<CashRegisterBalanceTransaction[]> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Payment/cash-registers/balance/${balanceId}/transactions`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as CashRegisterBalanceTransaction[];
    } catch {
      return [];
    }
  }

  public async getCancelledReceipts(balanceId: number): Promise<CancelledReceipt[]> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Payment/cash-registers/balance/${balanceId}/cancelled`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as CancelledReceipt[];
    } catch {
      return [];
    }
  }

  public async getCashReceipt(nrRecibo: number): Promise<CashReceipt | null> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Payment/cash-receipt/${nrRecibo}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return null;
      return response.data[0] as CashReceipt;
    } catch {
      return null;
    }
  }

  // Propaga o erro: "sem adiantamento" e "não deu para checar" não podem se confundir.
  public async getPatientAdvances(patientId: string, dias = 30, ultimos = 0): Promise<PatientAdvance[]> {
    const response = await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/Payment/patient/${encodeURIComponent(patientId)}/advances?dias=${dias}&ultimos=${ultimos}`)
        .pipe(catchError(this.handleError)),
    );
    if (!response?.data?.length) return [];
    return response.data[0] as PatientAdvance[];
  }

  // Propaga o erro: recusa e falha de rede não podem se confundir.
  public async postCashRefund(request: { nrAdiantamento: number; nrSeqCaixaRec: number; username: string }): Promise<void> {
    await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/Payment/cash/refund`, request)
        .pipe(catchError(this.handleError)),
    );
  }

  // Piloto por unidade. Erro de rede devolve false: melhor esconder a tela do que oferecer o
  // que o back vai recusar.
  public async getPagamentoAvulsoLiberado(unit: number): Promise<boolean> {
    try {
      return !!(await firstValueFrom(
        this.httpClient.get<boolean>(`${this.apiURL}/Payment/avulso/enabled?unit=${unit}`),
      ));
    } catch {
      return false;
    }
  }

  public async postTransferAdvance(
    request: { nrAdiantamento: number; cdPessoaDestino: string; username: string },
  ): Promise<void> {
    await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/Payment/advance/transfer`, request)
        .pipe(catchError(this.handleError)),
    );
  }

  // Propaga o erro: recusa do formato e falha de rede não podem se confundir.
  // username por query string, como GetEstablishments/GetCashRegisters: aqui não há DTO de corpo,
  // o corpo é multipart/form-data só com o arquivo.
  public async postTermoTransferencia(nrAdiantamento: number, arquivo: File, username: string): Promise<void> {
    const form = new FormData();
    form.append('file', arquivo, arquivo.name);

    await firstValueFrom(
      this.httpClient
        .post<any>(
          `${this.apiURL}/Payment/advance/${nrAdiantamento}/termo?username=${encodeURIComponent(username)}`,
          form,
        )
        .pipe(catchError(this.handleError)),
    );
  }

  // 404 é "não anexado", resposta esperada. Qualquer outra falha propaga: as duas não podem
  // se confundir na tela.
  public async getTermoTransferencia(nrAdiantamento: number): Promise<TermoTransferencia | null> {
    try {
      const response = await firstValueFrom(
        this.httpClient.get<any>(`${this.apiURL}/Payment/advance/${nrAdiantamento}/termo`),
      );
      if (!response?.data?.length) return null;
      return response.data[0] as TermoTransferencia;
    } catch (e: any) {
      if (e?.status === 404) return null;
      throw e;
    }
  }

  public async postAdyenRefund(request: { nrAdiantamento: number; username: string }): Promise<void> {
    await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/Payment/adyen/terminal/refund`, request)
        .pipe(catchError(this.handleError)),
    );
  }

  private async executeCashPayment(request: PostCashPaymentRequest): Promise<PostCashPaymentResult> {
    const response = await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/Payment/cash`, request)
        .pipe(catchError(this.handleError)),
    );
    if (!response?.data?.length) {
      throw new Error('Pagamento não retornado pela API.');
    }
    return response.data[0] as PostCashPaymentResult;
  }

  private handleError(error: HttpErrorResponse) {
    return throwError(() => error);
  }
}
